import { useEffect, useId, useRef, useState } from 'react';
import type { ChangeEvent, ClipboardEvent, KeyboardEvent, ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { Typography } from '../Typography';

// ─── Constants ────────────────────────────────────────────────────────────────

const OTP_LENGTH = 6;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OtpInputProps {
  /** Called once when all six digits are entered. Not re-fired for the same code unless a digit is cleared first. */
  onComplete: (code: string) => void;
  /** Label rendered above the inputs. */
  label?: string;
  /** Error message — activates error styling and announced via aria-live. */
  error?: ReactNode;
  /** Disables all inputs. No interaction possible. */
  disabled?: boolean;
  /** Marks inputs as non-interactive while a verification request is in flight. */
  isVerifying?: boolean;
  /** Pre-populates the inputs on mount. Useful for stories and testing. */
  defaultValue?: string;
  /** Focuses the first input on mount. */
  autoFocus?: boolean;
  /** Change to clear digits and completion tracking, restoring focus without remounting. */
  resetKey?: number;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const cellBase =
  'w-full min-w-0 h-9 text-center rounded-small border numeric-large bg-surface text-body focus-visible:outline-none focus:!border-interactive-loud transition-colors duration-150 selection:bg-transparent';

const cellDefault = 'border-default';
const cellError = 'border-error bg-error text-error';
const cellDisabled = 'border-default bg-disabled text-disabled cursor-not-allowed';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer OtpInput component.
 *
 * Renders six individual digit boxes for one-time passcode entry.
 * Handles typing, deletion, and paste.
 *
 * ### Behavior
 * - Typing a digit advances focus to the next box.
 * - Backspace on an empty box moves focus to the previous box.
 * - Pasting a numeric string fills from the first box.
 * - `onComplete` fires once per unique six-digit code; cleared digits reset the guard.
 *
 * ### States
 * - **Default** — editable, `border-default`
 * - **Error** — `border-error bg-error`; message announced via `aria-live`
 * - **Disabled** — fully non-interactive
 * - **Verifying** — non-interactive while an API call is in flight
 *
 * @example
 * <OtpInput label="Enter your code" onComplete={(code) => verify(code)} />
 *
 * @example
 * // Error after incorrect submission
 * <OtpInput error="Incorrect code — please try again" onComplete={verify} />
 */
export function OtpInput({
  onComplete,
  label,
  error,
  disabled = false,
  isVerifying = false,
  defaultValue = '',
  autoFocus = false,
  resetKey = 0,
}: OtpInputProps) {
  const groupId = useId();
  const errorId = `${groupId}-error`;

  const [digits, setDigits] = useState<string[]>(() => {
    const seed = defaultValue.replace(/\D/g, '').slice(0, OTP_LENGTH);
    return Array.from({ length: OTP_LENGTH }, (_, i) => seed[i] ?? '');
  });

  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(OTP_LENGTH).fill(null));
  const focusedIndexRef = useRef<number | null>(null);
  // Tracks every code completed since the last clear, not just the most recent one,
  // so overwriting back to an earlier completed code (without clearing) can't re-fire it.
  const completedCodesRef = useRef<Set<string>>(new Set());

  const isDisabled = disabled || isVerifying;
  const hasError = Boolean(error);
  const wasDisabledRef = useRef(isDisabled);
  const previousResetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (wasDisabledRef.current && !isDisabled && focusedIndexRef.current !== null) {
      inputRefs.current[focusedIndexRef.current]?.focus();
    }
    wasDisabledRef.current = isDisabled;
  }, [isDisabled]);

  useEffect(() => {
    if (previousResetKeyRef.current === resetKey) return;
    previousResetKeyRef.current = resetKey;
    setDigits(Array<string>(OTP_LENGTH).fill(''));
    completedCodesRef.current.clear();
    focusedIndexRef.current = 0;
    if (!isDisabled) inputRefs.current[0]?.focus();
  }, [resetKey, isDisabled]);

  // ── Helpers ─────────────────────────────────────────────────────────────

  const focusInput = (index: number) => {
    inputRefs.current[Math.max(0, Math.min(index, OTP_LENGTH - 1))]?.focus();
  };

  const tryComplete = (newDigits: string[]) => {
    if (newDigits.some((d) => d === '')) return;
    const code = newDigits.join('');
    if (completedCodesRef.current.has(code)) return;
    completedCodesRef.current.add(code);
    onComplete(code);
  };

  const distributeFromStart = (chars: string) => {
    const newDigits = Array<string>(OTP_LENGTH).fill('');
    chars.split('').forEach((ch, i) => {
      if (i < OTP_LENGTH) newDigits[i] = ch;
    });
    setDigits(newDigits);
    if (chars.length < OTP_LENGTH) completedCodesRef.current.clear();
    const lastFilled = Math.min(chars.length - 1, OTP_LENGTH - 1);
    focusInput(lastFilled < OTP_LENGTH - 1 ? lastFilled + 1 : OTP_LENGTH - 1);
    tryComplete(newDigits);
  };

  // ── Event handlers ───────────────────────────────────────────────────────

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (/^[0-9]$/.test(e.key)) {
      // Prevent the browser from inserting the character — we own the value.
      e.preventDefault();
      const newDigits = [...digits];
      newDigits[index] = e.key;
      setDigits(newDigits);
      if (index < OTP_LENGTH - 1) focusInput(index + 1);
      tryComplete(newDigits);
      return;
    }
    switch (e.key) {
      case 'Backspace':
        e.preventDefault();
        if (digits[index]) {
          const newDigits = [...digits];
          newDigits[index] = '';
          setDigits(newDigits);
          completedCodesRef.current.clear();
        } else if (index > 0) {
          const newDigits = [...digits];
          newDigits[index - 1] = '';
          setDigits(newDigits);
          completedCodesRef.current.clear();
          focusInput(index - 1);
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (index > 0) focusInput(index - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (index < OTP_LENGTH - 1) focusInput(index + 1);
        break;
    }
  };

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    // Mobile virtual keyboards may not honour onKeyDown preventDefault; handle
    // single-digit entry here as a fallback. Also handles Backspace clearing
    // (the browser fires onChange with an empty value on a filled box).
    const raw = e.target.value;
    const incoming = raw.replace(/\D/g, '').slice(0, OTP_LENGTH);

    // Mobile one-time-code autofill may inject the complete code into one input.
    if (incoming.length > 1) {
      distributeFromStart(incoming);
      return;
    }

    // A non-digit key (physical keyboard) typed over a filled box — revert, don't clear it.
    if (!incoming && raw !== '') {
      e.target.value = digits[index];
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = incoming;
    setDigits(newDigits);

    if (!incoming) {
      completedCodesRef.current.clear();
      return;
    }

    if (index < OTP_LENGTH - 1) focusInput(index + 1);
    tryComplete(newDigits);
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    distributeFromStart(pasted);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex w-full flex-col gap-2">
      {label && (
        <Typography variant="formLabel" size="default" as="label" htmlFor={`${groupId}-0`}>
          {label}
        </Typography>
      )}

      <div
        role="group"
        aria-label={label ?? 'One-time passcode'}
        aria-describedby={hasError ? errorId : undefined}
        className="grid w-full grid-cols-6 gap-2"
      >
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            id={i === 0 ? `${groupId}-0` : undefined}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            autoFocus={autoFocus && i === 0}
            aria-label={`Digit ${i + 1} of ${OTP_LENGTH}`}
            aria-invalid={hasError ? true : undefined}
            value={digit}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={(e) => {
              focusedIndexRef.current = i;
              e.target.select();
            }}
            disabled={isDisabled}
            className={cn(cellBase, hasError ? cellError : isDisabled ? cellDisabled : cellDefault)}
          />
        ))}
      </div>

      <Typography
        id={errorId}
        variant="body"
        size="medium"
        color="error"
        aria-live="polite"
        className={cn('w-full text-left', !error && 'invisible')}
      >
        {error ?? '\u00a0'}
      </Typography>
    </div>
  );
}

export default OtpInput;
